import { useEffect, type RefObject } from "react";

/** H3: how often the watermark jumps to a new place. */
export const WATERMARK_MOVE_MS = 4_000;
export const WATERMARK_OPACITY = "0.3";
export const WATERMARK_TEST_ID = "video-watermark";

/** Today as YYYY-MM-DD on this device's calendar. */
export function watermarkDate(now: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export function watermarkText(email: string, now: Date = new Date()): string {
  return `${email} · ${watermarkDate(now)}`;
}

/** The properties that keep it visible and unclickable; re-applied whenever one is changed. */
const BASE_STYLE: Record<string, string> = {
  position: "absolute",
  display: "block",
  visibility: "visible",
  opacity: WATERMARK_OPACITY,
  "pointer-events": "none",
  "user-select": "none",
  "z-index": "2147483000",
  color: "#ffffff",
  "text-shadow": "0 0 3px #000000, 0 0 1px #000000",
  "font-size": "clamp(11px, 2.2vw, 18px)",
  "font-family": "system-ui, sans-serif",
  "white-space": "nowrap",
  transform: "none",
};

/** What hiding it or making it clickable would change (every browser supports these). */
const GUARDED = ["position", "display", "visibility", "opacity", "pointer-events", "z-index"];

// ah-fix P3-5: inline `!important` beats every page stylesheet (author rules, even `!important`).
const IMPORTANT = "important";

function tampered(el: HTMLElement, text: string): boolean {
  if (el.hidden || el.textContent !== text) return true;
  return GUARDED.some(
    (prop) =>
      el.style.getPropertyValue(prop) !== BASE_STYLE[prop] ||
      el.style.getPropertyPriority(prop) !== IMPORTANT,
  );
}

/**
 * ah-fix P3-5: what the browser actually shows. A user-level style (an ad-blocker's cosmetic
 * filter) still outranks inline `!important`, and no mutation is fired for it, so every tick
 * checks the computed style too.
 */
function hiddenOnScreen(el: HTMLElement): boolean {
  const shown = window.getComputedStyle(el);
  return (
    shown.display === "none" ||
    shown.visibility === "hidden" ||
    Number.parseFloat(shown.opacity || "1") < Number.parseFloat(WATERMARK_OPACITY) / 2
  );
}

function applyBase(el: HTMLElement, text: string) {
  el.hidden = false;
  if (el.textContent !== text) el.textContent = text;
  for (const [prop, value] of Object.entries(BASE_STYLE)) {
    el.style.setProperty(prop, value, IMPORTANT);
  }
}

function moveTo(el: HTMLElement) {
  // Anywhere over the picture, keeping the text inside the frame.
  el.style.setProperty("top", `${(5 + Math.random() * 80).toFixed(1)}%`, IMPORTANT);
  el.style.setProperty("left", `${(3 + Math.random() * 60).toFixed(1)}%`, IMPORTANT);
}

/**
 * A fresh element. Outside tests it carries no fixed id or test id, only a random attribute name,
 * so a saved filter for "the watermark" has nothing stable to match.
 */
function createMark(): HTMLElement {
  const el = document.createElement("div");
  if (import.meta.env.MODE !== "production") el.setAttribute("data-testid", WATERMARK_TEST_ID);
  el.setAttribute(`data-w${Math.random().toString(36).slice(2, 8)}`, "");
  el.setAttribute("aria-hidden", "true");
  return el;
}

/**
 * H3 measure 3: the student's email and today's date float faintly over the video and move every
 * few seconds, so a screen recording shows whose account it came from. The element is created
 * here, outside React, and watched: removed from the page it is put back; hidden, faded or made
 * clickable through its style, the style is restored (inline `!important`); hidden by a style it
 * cannot outrank, it is replaced by a new element on the next tick. A deterrent, not DRM — see
 * decisions §32.
 */
export function useVideoWatermark(
  containerRef: RefObject<HTMLElement | null>,
  email: string | null | undefined,
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!container || !email) return;
    let el = createMark();
    let text = watermarkText(email);
    applyBase(el, text);
    moveTo(el);
    container.appendChild(el);

    const restore = () => {
      if (tampered(el, text)) applyBase(el, text);
      if (el.parentNode !== container) container.appendChild(el);
    };
    const observer = new MutationObserver(restore);
    const watch = () => {
      observer.observe(container, { childList: true });
      observer.observe(el, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true,
      });
    };
    watch();

    const timer = setInterval(() => {
      text = watermarkText(email);
      restore();
      if (hiddenOnScreen(el)) {
        observer.disconnect();
        el.remove();
        el = createMark();
        applyBase(el, text);
        container.appendChild(el);
        watch();
      }
      moveTo(el);
    }, WATERMARK_MOVE_MS);

    return () => {
      clearInterval(timer);
      observer.disconnect();
      el.remove();
    };
  }, [containerRef, email]);
}
